import { Collapse, List, Skeleton } from 'antd'
import NoDataBadge from 'atom/Badge'
import { DateTimeV2 } from 'components/date-time'
import { TransactionStateBadge } from 'components/transactions/transaction-state-badge'
import { TransactionsCollapseWrapper } from '../../../../atom/transactions'
import { renderFormatMessage } from '../../../../helpers/intl'
import { Transaction } from '../../../../models/transaction'
import { HomePageTransactionOverviewContainer, secondsToTime } from './transaction-overview'

interface props {
  transactions: Transaction[]
}

const TransactionOverviewCollapse: React.FC<props> = ({ transactions }) => {
  const disconnected = ({ endAt, timezone }: Transaction) =>
    endAt ? <DateTimeV2 date={endAt} timezone={timezone} isSidePanel /> : <NoDataBadge noBackground />

  return (
    <Skeleton loading={false}>
      <HomePageTransactionOverviewContainer>
        <TransactionsCollapseWrapper>
          <Collapse accordion bordered={false} expandIconPosition="right">
            {transactions.map((transaction) => (
              <Collapse.Panel
                key={transaction.id}
                header={
                  <div
                    className="paragraph-01-bold"
                    style={{
                      minHeight: '12px',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {renderFormatMessage('dashboard.transactionsPage.table.transactionId', 'Transaction ID')}
                  </div>
                }
                extra={transaction.id}
              >
                <List
                  dataSource={[transaction]}
                  renderItem={(transaction) => (
                    <>
                      <List.Item
                        actions={[
                          <div className={`state ${transaction.startMethod.toLowerCase()} paragraph-01-regular`}>
                            {transaction.startMethod}
                          </div>,
                        ]}
                      >
                        <List.Item.Meta
                          key={`${transaction.startMethod}-tenant`}
                          title={<div className="paragraph-01-bold">Start Method</div>}
                        />
                      </List.Item>
                      <List.Item
                        actions={[<div className="paragraph-01-regular">{transaction.user?.name || '-'}</div>]}
                      >
                        <List.Item.Meta
                          key={`${transaction.user?.name}-tenant`}
                          title={<div className="paragraph-01-bold">User</div>}
                        />
                      </List.Item>
                      <List.Item
                        actions={[
                          <div className="paragraph-01-regular">
                            {secondsToTime(transaction.currentChargingDuration).h}h{' '}
                            {secondsToTime(transaction.currentChargingDuration).m}m{' '}
                            {secondsToTime(transaction.currentChargingDuration).s}s
                          </div>,
                        ]}
                      >
                        <List.Item.Meta
                          key={`${transaction.currentChargingDuration}-tenant`}
                          title={<div className="paragraph-01-bold">Plug-in Duration</div>}
                        />
                      </List.Item>
                      <List.Item
                        actions={[
                          <DateTimeV2 date={transaction.startAt} timezone={transaction.timezone} isSidePanel />,
                        ]}
                      >
                        <List.Item.Meta
                          key={`${transaction.id}-connected`}
                          title={renderFormatMessage('dashboard.transactionsPage.table.connected', 'Connected')}
                        />
                      </List.Item>
                      <List.Item actions={[disconnected(transaction)]}>
                        <List.Item.Meta
                          key={`${transaction.id}-disconnected`}
                          title={renderFormatMessage('dashboard.transactionsPage.table.disconnected', 'Disconnected')}
                        />
                      </List.Item>
                      <List.Item actions={[<div className="paragraph-01-regular">{transaction.energy} kWh</div>]}>
                        <List.Item.Meta
                          key={`${transaction.energy}-tenant`}
                          title={<div className="paragraph-01-bold">Energy(kWh)</div>}
                        />
                      </List.Item>
                      <List.Item actions={[<TransactionStateBadge transaction={transaction} />]}>
                        <List.Item.Meta
                          key={`${transaction.id}-currentState`}
                          title={renderFormatMessage('dashboard.transactionsPage.table.state', 'State')}
                        />
                      </List.Item>
                      <List.Item
                        actions={[
                          <div className="paragraph-01-regular">${(transaction.financial.total / 100).toFixed(2)}</div>,
                        ]}
                      >
                        <List.Item.Meta
                          key={`${transaction.financial.total}-tenant`}
                          title={
                            <div className="paragraph-01-bold">
                              {renderFormatMessage('dashboard.transactionsPage.table.total', 'Total')}
                            </div>
                          }
                        />
                      </List.Item>
                    </>
                  )}
                />
              </Collapse.Panel>
            ))}
          </Collapse>
        </TransactionsCollapseWrapper>
      </HomePageTransactionOverviewContainer>
    </Skeleton>
  )
}

export default TransactionOverviewCollapse
